/**
 * Phase 15.2 — Privacy-safe funnel telemetry.
 *
 * Operating-rule alignment: NO spec contents, NO PII, NO IPs, NO user-agents.
 * We count enum-typed events (scorecards run, grade buckets, share clicks,
 * verify clicks) and nothing else. The default sink is `console.debug` so dev
 * sees the line; production wires a real sink at boot (Workers Analytics
 * Engine, Plausible, etc.).
 *
 * Event shape: `{ event: enum, props: { grade: "A"|"B"|"C"|"D"|"F" } | etc. }`.
 * No free-form strings. Every event passes through `sanitize()` which drops
 * anything not on the allowlist.
 */

export type FunnelEvent =
  | { event: "scorecard.run"; props: { chip: "petstore" | "library" | "broken" | "pasted" } }
  | { event: "scorecard.grade"; props: { grade: "A" | "B" | "C" | "D" | "F" } }
  | { event: "scorecard.share.click"; props: { medium: "url" | "qr" } }
  | { event: "scorecard.verify.click"; props: { medium: "command" | "cosign" } }
  | { event: "attack.viewed" };

export type TelemetrySink = (evt: FunnelEvent) => void;

let sink: TelemetrySink = (e) => {
  if (typeof console !== "undefined" && console.debug) console.debug("[keystone-telemetry]", e);
};

export function setTelemetrySink(fn: TelemetrySink): void {
  sink = fn;
}

export function track(evt: FunnelEvent): void {
  // The TS shape is enforced at compile time; the runtime sanity check guards
  // against any future erosion (e.g., a `props.spec` field accidentally added).
  const sanitized = sanitize(evt);
  sink(sanitized);
}

const ALLOWED: Record<FunnelEvent["event"], string[]> = {
  "scorecard.run": ["chip"],
  "scorecard.grade": ["grade"],
  "scorecard.share.click": ["medium"],
  "scorecard.verify.click": ["medium"],
  "attack.viewed": []
};

function sanitize(evt: FunnelEvent): FunnelEvent {
  const allowedKeys = ALLOWED[evt.event];
  const e = evt as { event: FunnelEvent["event"]; props?: Record<string, unknown> };
  if (!e.props) return evt;
  const cleanProps: Record<string, unknown> = {};
  for (const k of allowedKeys) {
    if (k in e.props) cleanProps[k] = e.props[k];
  }
  return { event: e.event, props: cleanProps } as FunnelEvent;
}
